import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUserActivity } from "../../../services/users";
import { ActivityBadge, timeAgo } from "./accountBadges";

/** Days shown in the activity strip. */
export const STRIP_DAYS = 30;

const DAY = 24 * 60 * 60 * 1000;

/** UTC day key ("2026-10-15") of a date. */
const dayKey = (date) => new Date(date).toISOString().slice(0, 10);

/**
 * The last `count` days (oldest first) as `{ key, date, active }`,
 * `active` when the day is in `activeDays`.
 */
export function activityStrip(activeDays, count = STRIP_DAYS, now = Date.now()) {
  const active = new Set((activeDays || []).map(dayKey));
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(now - (count - 1 - index) * DAY);
    const key = dayKey(date);
    return { key, date, active: active.has(key) };
  });
}

/**
 * App activity of a user: last activity, days of use over the last 30 days
 * and one square per day. Falls back to `lastActiveAt` alone while the history is unavailable.
 */
const UserActivity = ({ userId, lastActiveAt }) => {
  const { t, i18n } = useTranslation();
  const [activity, setActivity] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setActivity(null);
    if (userId) {
      getUserActivity(userId).then((data) => {
        if (!cancelled) setActivity(data);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const last = activity?.lastActiveAt || lastActiveAt;
  const strip = activity ? activityStrip(activity.activeDays) : null;
  const activeCount = strip ? strip.filter((day) => day.active).length : 0;
  const trackedSince = activity?.trackedSince ? new Date(activity.trackedSince) : null;
  const dateFormat = { day: "numeric", month: "short" };

  return (
    <div className="user-activity">
      <h3 className="detail-section-title">{t("App activity")}</h3>

      <div className="user-activity-last">
        <ActivityBadge lastActiveAt={last} withDate={false} />
        <div>
          <span className="user-activity-label">{t("Last activity")}</span>
          {last ? (
            <strong title={new Date(last).toLocaleString(i18n.language)}>
              {new Date(last).toLocaleString(i18n.language, {
                dateStyle: "medium",
                timeStyle: "short",
              })}{" "}
              <span className="user-activity-ago">
                ({timeAgo(last, i18n.language)})
              </span>
            </strong>
          ) : (
            <strong>{t("Not recorded yet")}</strong>
          )}
        </div>
      </div>

      {strip && (
        <>
          <p className="user-activity-label">
            {t("activeDaysCount", { count: activeCount, days: STRIP_DAYS })}
          </p>
          <ol
            className="user-activity-strip"
            aria-label={t("Days of use over the last {{days}} days", {
              days: STRIP_DAYS,
            })}
          >
            {strip.map((day) => (
              <li
                key={day.key}
                data-active={day.active ? "" : undefined}
                title={`${day.date.toLocaleDateString(i18n.language, dateFormat)} · ${
                  day.active ? t("Used the app") : t("Not seen")
                }`}
              />
            ))}
          </ol>
          <div className="user-activity-axis" aria-hidden="true">
            <span>{strip[0].date.toLocaleDateString(i18n.language, dateFormat)}</span>
            <span>{t("Today")}</span>
          </div>
          {trackedSince && trackedSince > strip[0].date && (
            <p className="user-activity-note">
              {t("Activity tracked since {{date}}", {
                date: trackedSince.toLocaleDateString(i18n.language),
              })}
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default UserActivity;
